import { Button } from 'antd';
import { SystemLanguage, useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useStatus from 'hooks/useStatus';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { overseaTranslate } from 'services/api/overseaTranslate';

export type NeedTranslateLanguage = Extract<SystemLanguage, 'zh-CN' | 'en-US'>;

const needTranslateLanguage: NeedTranslateLanguage[] = ['zh-CN', 'en-US'];

export const isNeedTranslateLanguage = (
  language: SystemLanguage,
): language is NeedTranslateLanguage => {
  return needTranslateLanguage.includes(language as NeedTranslateLanguage);
};

export const langMap = {
  'zh-CN': 'cn',
  'en-US': 'en',
} as const;

export interface OperationRender {
  (props: { error: Error | null; loading: boolean; displayTranslation: boolean }): ReactNode;
}

export interface TranslationRender {
  (props: { operation: ReactNode; text: string; displayTranslation: boolean }): JSX.Element;
}

export interface TranslationProps {
  originText: string;
  translationMap?: Review.Translation;
  children: TranslationRender;
  operationRender?: OperationRender;
  afterTranslation?: (lang: 'cn' | 'en', text: string) => void;
}

function Translation(props: TranslationProps) {
  const { t } = useTranslate();

  const defaultOperationRender: OperationRender = useCallback(
    (props) => {
      const { error, loading, displayTranslation } = props;

      if (loading) {
        return (
          <Button type="link" loading>
            {t('trade.translatingMsg')}
          </Button>
        );
      }

      if (error) {
        return <Button type="link">{t('trade.翻译出错，点击重翻')}</Button>;
      }

      return (
        <Button type="link">
          {displayTranslation ? t('trade.查看原文') : t('trade.查看翻译')}
        </Button>
      );
    },
    [t],
  );

  const {
    originText,
    translationMap: originTranslationMap = {},
    children,
    operationRender = defaultOperationRender,
    afterTranslation,
  } = props;

  const systemLanguage = useSystemLanguageValue();

  const needTranslate = isNeedTranslateLanguage(systemLanguage);

  const [translationMap, setTranslationMap] = useState(originTranslationMap);

  const [displayTranslation, setDisplayTranslation] = useState(needTranslate);

  const { pending, error, done } = useStatus();

  const onTranslate = useCallback(() => {
    if (!isNeedTranslateLanguage(systemLanguage)) {
      return Promise.resolve();
    }

    done.pending();

    return overseaTranslate(originText, systemLanguage, 'review')
      .then((res) => {
        setTranslationMap((map) => {
          return {
            ...map,
            [langMap[systemLanguage]]: res,
          };
        });

        done();

        if (afterTranslation) {
          afterTranslation(langMap[systemLanguage], res);
        }
      })
      .catch((err) => {
        done.error(err);
      });
  }, [done, originText, systemLanguage, afterTranslation]);

  const text = useMemo(() => {
    if (!isNeedTranslateLanguage(systemLanguage)) {
      return originText;
    }

    if (!displayTranslation) {
      return originText;
    }

    const translation = translationMap[langMap[systemLanguage]];

    if (!translation) {
      return originText;
    }

    return translation;
  }, [systemLanguage, displayTranslation, translationMap, originText]);

  const operation = useMemo(() => {
    const node = operationRender({
      error,
      loading: pending,
      displayTranslation,
    });

    if (!needTranslate) {
      return null;
    }

    if (pending) {
      return node;
    }

    return <div onClick={() => setDisplayTranslation((display) => !display)}>{node}</div>;
  }, [operationRender, error, pending, needTranslate, displayTranslation]);

  const refsCurrent = {
    onTranslate,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (
      !isNeedTranslateLanguage(systemLanguage) ||
      !displayTranslation ||
      translationMap[langMap[systemLanguage]]
    ) {
      return;
    }

    const {
      current: { onTranslate },
    } = refs;

    onTranslate();
  }, [translationMap, systemLanguage, displayTranslation]);

  return children({
    text,
    operation,
    displayTranslation: Boolean(
      displayTranslation &&
        isNeedTranslateLanguage(systemLanguage) &&
        translationMap[langMap[systemLanguage]],
    ),
  });
}

export default Translation;
